import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { Skeleton } from '@mui/material';
import UserHeader from '@/components/User/UserHeader';
import ItemList from './ItemList';
import Categories from './Categories';
import Navigation from '@/components/Navigation/Navigation';
import items from '@/api/items';
import FilterOrder from '@/components/Order/FilterOrder';
import pos from '@/api/pos';

const { getItems, getCollections, getInnerCollections, getHomeCollections } = items();
const { getProducts } = pos();

export default function Home() {
	const dispatch = useDispatch();
	const selectedOutlet = useSelector((state) => state.auth.selectedOutlet);
	const historyCollection = useSelector((state) => state.cart.historyCollection);
	const categoryFilter = useSelector((state) => state.cart.categoryFilter);
	const orderDate = useSelector((state) => state.cart.orderDate);
	// Active child only — never auth.user.customer for menu scope
	const selectedCustomer = useSelector((state) => state.auth.selectedCustomer);
	const customerId = selectedCustomer?.id ?? null;
	const outletId = selectedOutlet?.id ?? null;

	const [search, setSearch] = useState('');
	const [searchParam] = useDebounce(search, 500);

	const currDate = useMemo(() => {
		return dayjs(orderDate).format('YYYY-MM-DD');
	}, [orderDate]);

	// Reset drill-down when the active child changes so the previous class menu does not stick
	useEffect(() => {
		dispatch({ type: 'cart/setHistoryCollection', payload: { type: 'clear' } });
		dispatch({ type: 'cart/setCategoryFilter', payload: null });
		setSearch('');
	}, [customerId, dispatch]);

	const objParam = useMemo(() => {
		const obj = { outlet: outletId, date: currDate, max_size: true, ordering: 'index' };
		if (customerId != null) {
			obj.customer_id = customerId;
		}
		if (searchParam) {
			obj.search = searchParam;
		}
		return obj;
	}, [currDate, outletId, searchParam, customerId]);

	const collectionParams = useMemo(() => {
		const newObjParam = { ...objParam };
		if (historyCollection?.length) {
			newObjParam.parent_collections = historyCollection[historyCollection.length - 1]?.id;
		}
		if (categoryFilter && categoryFilter === 'x') {
			newObjParam.ordering = 'product__name';
		}
		return newObjParam;
	}, [objParam, historyCollection, categoryFilter]);

	const itemParams = useMemo(() => {
		const newObjParam = { ...objParam };
		if (historyCollection?.length) {
			newObjParam.product__collections = historyCollection[historyCollection.length - 1]?.id;
			if (categoryFilter && categoryFilter !== 'x') {
				newObjParam.product__category = categoryFilter;
			}
		} else if (categoryFilter && categoryFilter !== 'x') {
			newObjParam.product__category = categoryFilter;
		}
		if (categoryFilter && categoryFilter === 'x') {
			newObjParam.ordering = 'product__name';
		}
		return newObjParam;
	}, [objParam, historyCollection, categoryFilter]);

	const { isFetched: isFCD } = useQuery({
		queryKey: ['collection-ids', outletId, customerId],
		enabled: !!outletId && customerId != null,
		queryFn: () => getCollections({ outlet: outletId, customer_id: customerId, max_size: true }),
		refetchOnWindowFocus: false,
	});

	const allProductsPram = useMemo(() => {
		const params = {
			collections__outletcollection__outlet: outletId || [],
			date: currDate,
			max_size: true,
			ordering: 'name',
		};
		if (customerId != null) {
			params.customer_id = customerId;
		}
		if (historyCollection?.length) {
			params.collections = historyCollection[historyCollection.length - 1]?.id;
			if (categoryFilter && categoryFilter !== 'x') {
				params.category = categoryFilter;
			}
		}
		if (searchParam) {
			params.search = searchParam;
		}
		return params;
	}, [outletId, currDate, historyCollection, categoryFilter, searchParam, customerId]);

	const fetchCollections = !!outletId && !!isFCD && categoryFilter !== 'x' && historyCollection?.length > 0 && !searchParam;
	const fetchHomeCollections = !!outletId && !!isFCD && categoryFilter !== 'x' && !historyCollection?.length && !searchParam;

	const {
		data: c,
		isFetching: isFC,
		isFetched: isFD,
	} = useQuery({
		queryKey: ['collections', collectionParams, customerId],
		enabled: fetchCollections && customerId != null,
		queryFn: () => getInnerCollections(collectionParams),
		refetchOnWindowFocus: false,
	});

	const {
		data: hc,
		isFetching: isFHC,
		isFetched: isFHD,
	} = useQuery({
		queryKey: ['home-collections', outletId, customerId, collectionParams],
		enabled: fetchHomeCollections && customerId != null,
		queryFn: () => getHomeCollections({ outlet: outletId, customer_id: customerId }),
		refetchOnWindowFocus: false,
	});

	const fetchHomeProducts = !!isFHD && !!outletId && !!isFCD && categoryFilter !== 'x' && !historyCollection?.length && !searchParam;

	const {
		data: d,
		isFetching: isFI,
		isFetched,
	} = useQuery({
		queryKey: ['items', itemParams, customerId],
		enabled: fetchHomeProducts && customerId != null,
		queryFn: () => getItems(itemParams),
		refetchOnWindowFocus: false,
	});

	const fetchProducts = (categoryFilter && categoryFilter === 'x') || (categoryFilter !== 'x' && historyCollection?.length > 0) || !!searchParam;
	const {
		data: dAll,
		isFetching: isFA,
		isFetchedAll,
	} = useQuery({
		queryKey: ['all-product-items', allProductsPram, customerId],
		enabled: fetchProducts && customerId != null,
		queryFn: () => getProducts(allProductsPram),
		refetchOnWindowFocus: false,
	});

	const isStillFetching = isFC || isFHC || isFI || isFA;
	const isDoneFetched = isFD || isFHD || isFetched || isFetchedAll;

	const itemsList =
		useMemo(() => {
			const col =
				c?.data?.results.map((cc) => {
					return { ...cc, collection: { ...cc }, name: cc.name };
				}) || [];
			const hCol =
				hc?.data?.results.map((cc) => {
					return { ...cc, name: cc.collection.name };
				}) || [];
			const itm =
				d?.data?.results.map((dd) => {
					return { ...dd, name: dd.product.name };
				}) || [];
			const itmAll =
				dAll?.data?.results.map((dd) => {
					return {
						...dd,
						name: dd.name,
						product: {
							...dd,
						},
					};
				}) || [];
			let dt = [];

			if (fetchHomeCollections && fetchHomeProducts) {
				dt = [...hCol, ...itm];
			} else if (fetchCollections && fetchProducts) {
				dt = [...col, ...itmAll];
			} else if (categoryFilter === 'x' && fetchProducts) {
				dt = itmAll;
			} else {
				dt = itmAll;
			}

			return dt;
		}, [c, hc, d, dAll, fetchCollections, fetchHomeCollections, fetchHomeProducts, fetchProducts, categoryFilter]) || [];

	useEffect(() => {
		dispatch({ type: 'cart/setSelectedItem', payload: null });
	}, [dispatch]);

	return (
		<div className='app-container'>
			<div className='flex flex-col w-full h-full gap-4 p-4'>
				<UserHeader />
				<FilterOrder search={search} setSearch={setSearch} />
				<Categories />

				<div className='relative food-grids scrollable'>
					<ItemList
						itemsList={itemsList}
						onCallbackAfterInsert={() => {
							setSearch(null);
						}}
					/>
					{itemsList.length === 0 && isStillFetching && (
						<>
							<p className='sr-only' role='status'>
								Loading menu
							</p>
							{Array.from({ length: 6 }, (_, index) => (
								<div key={index} className='food-cards food-skeleton' aria-hidden='true'>
									<div>
										<Skeleton variant='rectangular' width='100%' height={112} animation='wave' />
									</div>
									<div>
										<Skeleton variant='text' width='80%' sx={{ mx: 'auto' }} />
										<Skeleton variant='text' width='45%' sx={{ mx: 'auto' }} />
									</div>
								</div>
							))}
						</>
					)}
					{!isStillFetching && isDoneFetched && itemsList.length === 0 && (
						<span className='absolute top-1/2 left-1/2'>Sorry, no pre-orders available</span>
					)}
				</div>
			</div>
			<Navigation />
		</div>
	);
}
